import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  inject,
  OnInit,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { EMPTY, switchMap } from 'rxjs';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { TranslocoPipe } from '@jsverse/transloco';
import { DualLangInputComponent } from '@areeb-innovative-technologies/angular_multi_languages_input_lib';
import { JobTitleService } from '../../job-title/job-title.service';
import { DepartmentService } from '../../departments/department.service';
import { PositionService } from '../position.service';
import { PositionDetail, PositionWritePayload } from '../position.model';
import { OverlayLoaderComponent } from '../../../../../common/components/overlay-loader/overlay-loader.component';
import { ScrollToErrorDirective } from '../../../../../common/directives/scroll-to-error/scroll-to-error.directive';
import { InputComponent } from '../../../../../../ui-components/lib/input/input.component';
import { SelectComponent } from '../../../../../../ui-components/lib/select/select.component';
import { PaginatedSelectComponent } from '../../../../../../ui-components/lib/paginated-select/paginated-select.component';
import { DatePickerComponent } from '../../../../../../ui-components/lib/date-picker/date-picker.component';
import { ValidationMessageComponent } from '../../../../../../ui-components/lib/validation-message/validation-message.component';
import { TenantPathPipe } from '../../../../../common/pipes/tenant-path/tenant-path.pipe';
import { FormService } from '../../../../../../form-builder/services/form/form.service';
import { ToasterService } from '../../../../../../ui-components/services/toast/toast.service';
import { VariableService } from '../../../variables/variable.service';
import { TenantService } from '../../../../../common/services/tenant/tenant.service';
import { ADMIN_ROUTES } from '../../../../../common/constants/admin.routing.constants';
import { OptionItem } from '../../../../../../ui-components/lib/option.interface';
import { ENDPOINTS } from '../../../../../../core/common/constants/endpoints.constants';
import { LookupOptionItem } from '../../../../../../ui-components/lib/lookup-field/lookup-field-item';
import { dualLanguageCustomValidators } from '../../../../../../form-builder/services/form/custom-validators';
import { FeatureFlagsService } from '../../../../../../core/common/services/feature-flags/feature-flags.service';
import { FEATURE_FLAGS } from '../../../../../../core/common/constants/feature-flags.constants';
import { Status } from '../../../../../common/enums/user-status';

@Component({
  selector: 'lib-position-form',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    TranslocoPipe,
    OverlayLoaderComponent,
    InputComponent,
    SelectComponent,
    PaginatedSelectComponent,
    DatePickerComponent,
    ValidationMessageComponent,
    DualLangInputComponent,
    RouterLink,
    ScrollToErrorDirective,
    TenantPathPipe,
  ],
  templateUrl: './position-form.component.html',
  styleUrl: './position-form.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class PositionFormComponent implements OnInit {
  readonly ADMIN_ROUTES = ADMIN_ROUTES;

  private readonly positionService = inject(PositionService);
  private readonly jobTitleService = inject(JobTitleService);
  private readonly departmentService = inject(DepartmentService);
  private readonly formService = inject(FormService);
  private readonly toaster = inject(ToasterService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly tenantService = inject(TenantService);
  private readonly variableService = inject(VariableService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly featureFlagService = inject(FeatureFlagsService);

  readonly isPositionStatuesEnhancementEnabled = signal(
    this.featureFlagService.getFeatureFlagValue(
      FEATURE_FLAGS.ADMIN.USER_MANAGEMENT.position_statues_enhancement
    )
  ).asReadonly();

  scrollToError = viewChild.required(ScrollToErrorDirective);

  positionFormGroup = signal<FormGroup>(new FormGroup({}));
  editMode = signal<boolean>(false);
  isLoading = signal<boolean>(false);
  isPageLoading = signal<boolean>(false);
  isFormChanged = signal<boolean>(false);
  radfordGrade = signal<string>('');
  careerTrack = signal<string>('');
  positionStatusOptions = signal<OptionItem[]>([]);
  legalEntityOptions = signal<OptionItem[]>([]);
  reportingPositionApi = signal<string>(
    `${ENDPOINTS.Lookup.Positions}?filters[state]=active`
  );

  selectedDepartment!: LookupOptionItem;
  selectedJobTitle!: LookupOptionItem;
  selectedReportsToPosition!: LookupOptionItem;
  selectedIncumbent!: LookupOptionItem;

  readonly departmentApi = `${ENDPOINTS.Lookup.Departments}?filters[state]=active`;
  readonly jobTitleApi = `${ENDPOINTS.Lookup.JobTitles}?filters[state]=active`;
  readonly incumbentApi = computed(
    () =>
      `${ENDPOINTS.Lookup.User}?filters[placement_type]=vacant_position&filters[not_states][0]=${Status.DELETED}`
  );

  private positionId = '';
  private initialFormValues: Record<string, unknown> = {};

  ngOnInit(): void {
    this.initForm();
    this.getLegalEntities();
    this.positionId = this.route.snapshot.params['id'] ?? '';
    this.editMode.set(!!this.positionId);
    if (this.editMode()) {
      this.reportingPositionApi.set(
        `${ENDPOINTS.Lookup.Positions}?filters[not]=${this.positionId}&filters[state]=active`
      );
    }
    // Load options first; patchForm is called inside once options are ready
    this.getPositionStatusOptions();
  }

  initForm(): void {
    this.positionFormGroup.set(
      new FormGroup({
        name: new FormGroup(
          {
            en: new FormControl('', [Validators.maxLength(255)]),
            ar: new FormControl('', [Validators.maxLength(255)]),
          },
          { validators: [dualLanguageCustomValidators.atLeastOneLangRequired] }
        ),
        identifier: new FormControl<string | null>(null, [
          Validators.maxLength(100),
        ]),
        legal_entity_id: new FormControl<number | null>(
          null,
          Validators.required
        ),
        department_id: new FormControl<number | null>(
          null,
          Validators.required
        ),
        job_title_id: new FormControl<number | null>(null, Validators.required),
        reports_to_position_id: new FormControl<number | null>(null),
        position_status_option_id: new FormControl<number | null>(null),
        fte: new FormControl<number | null>(null, [
          Validators.min(0),
          Validators.max(1),
        ]),
        incumbent_user_id: new FormControl<number | null>(null),
        effective_from: new FormControl<string | null>(null),
        effective_to: new FormControl<string | null>(null),
      })
    );
  }

  getPositionStatusOptions(): void {
    this.variableService
      .getVariables(1, 100, 'user_management')
      .pipe(
        switchMap(res => {
          const positionStatusVar = res.data.find(
            v => v.key === 'position_status'
          );
          if (!positionStatusVar) {
            if (this.editMode()) this.patchForm();
            return EMPTY;
          }
          return this.variableService.getVariableOptionList(
            1,
            100,
            String(positionStatusVar.id)
          );
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: optRes => {
          this.positionStatusOptions.set(
            optRes.data
              .filter(o => o.is_active)
              .map(o => ({ value: o.id ?? undefined, label: o.label }))
          );
        },
        complete: () => {
          if (this.editMode()) {
            this.patchForm();
          }
        },
      });
  }

  getLegalEntities(): void {
    this.departmentService
      .getLegalEntities()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: response => {
          this.legalEntityOptions.set(response.data);
        },
      });
  }

  onJobTitleSelect(selected: LookupOptionItem): void {
    if (selected?.value) {
      this.jobTitleService
        .getJobTitleDetails(String(selected.value))
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(res => {
          this.radfordGrade.set(res.data.radford_grade?.label ?? '');
          this.careerTrack.set(
            res.data.track?.label ?? res.data.career_track?.label ?? ''
          );
        });
    } else {
      this.radfordGrade.set('');
      this.careerTrack.set('');
    }
  }

  patchForm(): void {
    this.isPageLoading.set(true);
    this.positionService
      .getPositionDetails(this.positionId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: res => {
          const d: PositionDetail = res.data;
          this.positionFormGroup().patchValue({
            name: {
              en: d.translations.name.en,
              ar: d.translations.name.ar,
            },
            identifier: d.identifier,
            legal_entity_id: d.legal_entity_id,
            department_id: d.department_id,
            job_title_id: d.job_title_id,
            reports_to_position_id: d.reports_to_position_id,
            position_status_option_id: d.position_status_option_id,
            fte: d.fte,
            incumbent_user_id: d.incumbent_user_id,
            effective_from: d.effective_from,
            effective_to: d.effective_to,
          });
          this.radfordGrade.set(
            d.job_title?.radford_grade?.label ?? d.radford_grade?.label ?? ''
          );
          this.careerTrack.set(
            d.job_title?.track?.label ??
              d.job_title?.career_track?.label ??
              d.career_track?.label ??
              ''
          );
          this.selectedDepartment = {
            value: d.department_id ?? 0,
            label: d.department?.name ?? '',
          };
          this.selectedJobTitle = {
            value: d.job_title_id ?? 0,
            label: d.job_title?.name ?? '',
          };
          this.selectedReportsToPosition = {
            value: d.reports_to_position_id ?? 0,
            label: d.reports_to_position?.name ?? '',
          };
          this.selectedIncumbent = {
            value: d.incumbent_user_id ?? 0,
            label: d.incumbent?.name ?? '',
          };
          this.initialFormValues = this.positionFormGroup().getRawValue();
        },
        error: () => {
          this.isPageLoading.set(false);
        },
        complete: () => {
          this.changeDetection();
          this.isPageLoading.set(false);
        },
      });
  }

  addEditPosition(): void {
    this.formService.syncDualLangControls(this.positionFormGroup());

    if (this.positionFormGroup().invalid) {
      this.positionFormGroup().markAllAsTouched();
      this.scrollToError().scrollToFirstError(true);
      return;
    }

    this.isLoading.set(true);
    const formValue = this.positionFormGroup().getRawValue();
    const payload: PositionWritePayload = {
      name: { en: formValue.name.en, ar: formValue.name.ar },
      identifier: formValue.identifier,
      legal_entity_id: formValue.legal_entity_id,
      department_id: formValue.department_id,
      job_title_id: formValue.job_title_id,
      reports_to_position_id: formValue.reports_to_position_id,
      position_status_option_id: formValue.position_status_option_id,
      fte: formValue.fte,
      incumbent_user_id: formValue.incumbent_user_id,
      effective_from: formValue.effective_from,
      effective_to: formValue.effective_to,
    };

    const request$ = this.editMode()
      ? this.positionService.updatePosition(this.positionId, payload)
      : this.positionService.createPosition(payload);

    request$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      error: error => this.handleError(error),
      complete: () => {
        this.isLoading.set(false);
        if (this.editMode()) {
          this.router.navigate([
            this.tenantService.adminPath(
              `${ADMIN_ROUTES.POSITIONS.DETAILS}/${this.positionId}`
            ),
          ]);
        } else {
          this.router.navigate([
            this.tenantService.adminPath(ADMIN_ROUTES.POSITIONS.LIST),
          ]);
        }
      },
    });
  }

  checkForChanges(): void {
    const currentValues = this.positionFormGroup().getRawValue();
    this.isFormChanged.set(
      JSON.stringify(currentValues) !== JSON.stringify(this.initialFormValues)
    );
  }

  changeDetection(): void {
    this.positionFormGroup()
      .valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.checkForChanges();
      });
  }

  handleError(error: { message?: string; error?: { message?: string } }): void {
    this.isLoading.set(false);
    this.toaster.add(
      error.message ?? error.error?.message ?? '',
      '',
      '',
      'error',
      true,
      true
    );
  }
}
